import * as Yup from 'yup';
import {IFormConfig, IFormFieldConfig, IFormTabFieldConfig} from '@src/@interfaces/components/Form/IFormConfig';
import {
    Fields,
    FormFieldAvailableTypes,
    FormFieldConfigType,
    FormFieldValidationRules
} from '@src/@types/components/Form';
import {isNumber} from '../DotAccessor/types-utils';
import RulesRepository from './RulesRepository';
import {IYupSchemeBuilder} from '@src/@interfaces/services/YupSchemeBuilder/IYupSchemeBuilder';

export default class YupSchemeBuilder implements IYupSchemeBuilder {
    static DEFAULT_FIELD_TYPE = FormFieldAvailableTypes.TEXT;

    static FIELD_TYPES_MIXED = [
        FormFieldAvailableTypes.SELECT,
    ];

    static FIELD_TYPES_ARRAY = [
        FormFieldAvailableTypes.CHECKBOX_LIST,
        FormFieldAvailableTypes.RADIO_LIST,
    ];

    static FIELD_TYPES_TEXT = [
        FormFieldAvailableTypes.TEXT,
        FormFieldAvailableTypes.PASSWORD,
        FormFieldAvailableTypes.EMAIL,
        FormFieldAvailableTypes.TEXTAREA,
    ];

    static FIELD_TYPES_NUMBER = [
        FormFieldAvailableTypes.NUMBER,
        FormFieldAvailableTypes.PRICE,
    ];

    static FIELD_TYPES_BOOLEAN = [
        FormFieldAvailableTypes.SWITCH,
        FormFieldAvailableTypes.CHECKBOX,
    ];

    protected rulesRepository: RulesRepository | undefined;

    getSchemeByObject(object: Record<string, any>, path: string[] = []): Yup.AnyObjectSchema {
        const shape: typeof object = {};
        for (const key of Object.keys(object)) {
            const value: unknown = object[key];

            shape[key] = this.resolveScheme(value, [...path, key]);
        }

        return Yup.object().shape(shape);
    }

    getSchemeByFormConfig(config: IFormConfig, path: [] = []): Yup.AnyObjectSchema {
        const fields: Fields<any, IFormFieldConfig | IFormTabFieldConfig> = Object.assign(
            {},
            config.fields || {},
            config.tabs?.fields || {}
        );

        const shape: Record<keyof typeof fields, Yup.AnySchema> = {};
        for (const key of Object.keys(fields)) {
            const field = fields[key];

            shape[key] = this.resolveSchemeByFieldConfig(field, [...path, key]);
        }

        return Yup.object().shape(shape);
    }

    protected resolveScheme(value: unknown, path: string[]): Yup.AnySchema {
        const type = typeof value;
        if (value === null || type === 'function' || type === 'undefined' || type === 'symbol') {
            return this.resolveAnyScheme();
        }

        if (Array.isArray(value)) {
            return this.resolveArrayScheme(value, path);
        }

        if (type === 'object') {
            return this.resolveObjectScheme(value as Record<any, any>, path);
        }

        const key = path.at(-1) as string;
        const isIdField = key === 'id' || key.endsWith('_id');
        if (isIdField || type === 'number' || type === 'bigint') {
            return this.resolveNumberScheme();
        }

        if (type === 'boolean') {
            return this.resolveBooleanScheme();
        }

        return this.resolveStringScheme();
    }

    protected resolveSchemeByFieldConfig(field: IFormFieldConfig | IFormTabFieldConfig, path: string[]): Yup.AnySchema {
        const API = YupSchemeBuilder;
        let fieldType: FormFieldConfigType | 'mixed' = field.type || API.DEFAULT_FIELD_TYPE;

        const getOptionsType = (field: IFormFieldConfig | IFormTabFieldConfig) => {
            let options = field?.options;
            if (options && typeof options === 'object') {
                if ('url' in options || 'route' in options) {
                    return null;
                } else {
                    options = Object.keys(options);
                }
            }

            if (options && Array.isArray(options)) {
                if (isNumber(options.at(0))) {
                    return 'number' as FormFieldAvailableTypes.NUMBER;
                } else {
                    return 'text' as FormFieldAvailableTypes.TEXT;
                }
            }

            return null;
        };

        let scheme;
        if (API.FIELD_TYPES_MIXED.includes(fieldType as FormFieldAvailableTypes)) {
            if (fieldType === 'select' as FormFieldAvailableTypes.SELECT) {
                fieldType = getOptionsType(field) ?? 'mixed';
            }
        }

        if (API.FIELD_TYPES_TEXT.includes(fieldType as FormFieldAvailableTypes)) {
            scheme = this.resolveStringScheme();
        } else if (API.FIELD_TYPES_NUMBER.includes(fieldType as FormFieldAvailableTypes)) {
            scheme = this.resolveNumberScheme();
        } else if (API.FIELD_TYPES_BOOLEAN.includes(fieldType as FormFieldAvailableTypes)) {
            scheme = this.resolveBooleanScheme();
        } else if (API.FIELD_TYPES_ARRAY.includes(fieldType as FormFieldAvailableTypes)) {
            let options = field.options || [];
            if (typeof options === 'object') {
                options = ('route' in options || 'url' in options) ? [] : Object.keys(options);
            }

            scheme = this.resolveArrayScheme(typeof options === 'string' ? [] : options, path);
        } else {
            scheme = this.resolveAnyScheme();
        }

        let rules: Record<FormFieldValidationRules, (string | number)[]>;
        const validationRules = field.validationRules;
        if (Array.isArray(validationRules)) {
            rules = validationRules.reduce((res, rule) => {
                res[rule] = [];

                return res;
            }, {} as typeof rules);
        } else if (validationRules) {
            rules = validationRules;
        } else {
            rules = {} as typeof rules;
        }

        for (const name of Object.keys(rules)) {
            scheme = this.applyValidationRule(scheme, name, rules[name as FormFieldValidationRules]);
        }

        return scheme;
    }

    protected resolveArrayScheme(value: any[], path: string[]) {
        const scheme = Yup.array();
        if (value.length) {
            scheme.of(
                this.resolveScheme(value[0], [...path, '*'])
            );
        }

        return scheme;
    }

    protected resolveObjectScheme(value: Record<any, any>, path: string[]) {
        return this.getSchemeByObject(value, path);
    }

    protected resolveAnyScheme() {
        return Yup.mixed().nullable();
    }

    protected resolveNumberScheme() {
        return Yup.number();
    }

    protected resolveBooleanScheme() {
        return Yup.bool();
    }

    protected resolveStringScheme() {
        return Yup.string();
    }

    protected applyValidationRule<S extends Yup.AnySchema>(scheme: S, ruleDeclaration: string, params: (string | number)[] = []): S {
        const rule = this.repository.getByName(ruleDeclaration);
        if (!rule) {
            return scheme;
        }

        rule.mergeParameters(params);

        return rule.mutateScheme(scheme);
    }

    protected get repository() {
        if (!this.rulesRepository) {
            this.rulesRepository = new RulesRepository();
        }

        return this.rulesRepository;
    }
}
